import { useCallback, useEffect, useRef, useState } from 'react';
import { getSignedUrl } from '../services/api_utils';
import { acknowledgedGeneration } from '../utils/runProjection';

const publicRecord = (record) => {
  const result = { ...record };
  delete result.token;
  return result;
};

const resultIdentity = (scopeKey, targetId, run) => {
  if (!targetId || !run?.id) return null;
  const generation = acknowledgedGeneration(run);
  const requestedGeneration = generation === null ? -1 : generation;
  const gcsPath = typeof run.result_gcs_path === 'string' && run.result_gcs_path
    ? run.result_gcs_path
    : null;
  return {
    key: JSON.stringify([
      scopeKey,
      targetId,
      run.id,
      requestedGeneration,
      gcsPath,
    ]),
    scopeKey,
    targetId,
    runId: run.id,
    generation: requestedGeneration,
    gcsPath,
  };
};

// One lifecycle for replacing a Function run's bounded Firestore preview with
// its complete GCS result. A target is a single Function pane or one Function
// instance inside a View. `scopeKey` keeps otherwise identical targets in two
// different Views independent.
//
// The internal record is the authority for request deduplication:
//
//   preview -> loading -> ready
//                        -> error -> retry -> loading
//
// A generation becomes `ready` only after `onHydrated` accepts it. Requests are
// aborted and ignored whenever their scope, target, run, or generation changes.
export default function useRunResultHydration({ scopeKey, onHydrated }) {
  const renderedScopeKey = String(scopeKey || '');
  const scopeKeyRef = useRef(renderedScopeKey);
  const onHydratedRef = useRef(onHydrated);
  const recordsRef = useRef(new Map());
  const controllersRef = useRef(new Map());
  const [states, setStates] = useState({});

  // Assign during render so an old response is invalidated immediately, before
  // the scope-change effect gets a chance to abort its network request.
  scopeKeyRef.current = renderedScopeKey;
  onHydratedRef.current = onHydrated;

  const publishRecord = useCallback((targetId, record) => {
    if (record) {
      recordsRef.current.set(targetId, record);
      setStates(prev => ({ ...prev, [targetId]: publicRecord(record) }));
      return;
    }

    recordsRef.current.delete(targetId);
    setStates(prev => {
      if (!Object.prototype.hasOwnProperty.call(prev, targetId)) return prev;
      const next = { ...prev };
      delete next[targetId];
      return next;
    });
  }, []);

  const clear = useCallback((targetId = null) => {
    if (targetId !== null) {
      controllersRef.current.get(targetId)?.abort();
      controllersRef.current.delete(targetId);
      publishRecord(targetId, null);
      return;
    }

    controllersRef.current.forEach(controller => controller.abort());
    controllersRef.current.clear();
    recordsRef.current.clear();
    setStates({});
  }, [publishRecord]);

  // Call whenever a bounded preview is installed. This deliberately forgets a
  // previous success for the same run: the caller has just replaced that full
  // result with a preview, so it genuinely needs hydrating again.
  const prepare = useCallback((targetId, run) => {
    const identity = resultIdentity(scopeKeyRef.current, targetId, run);
    if (!identity) {
      clear(targetId);
      return;
    }
    controllersRef.current.get(targetId)?.abort();
    controllersRef.current.delete(targetId);
    publishRecord(targetId, {
      ...identity,
      status: 'preview',
      error: null,
      token: null,
    });
  }, [clear, publishRecord]);

  const hydrate = useCallback(async (targetId, run, { force = false } = {}) => {
    const identity = resultIdentity(scopeKeyRef.current, targetId, run);
    if (!identity?.gcsPath) return;

    const existing = recordsRef.current.get(targetId);
    if (
      !force
      && existing?.key === identity.key
      && ['loading', 'ready', 'error'].includes(existing.status)
    ) {
      return;
    }

    controllersRef.current.get(targetId)?.abort();
    const controller = new AbortController();
    const token = Symbol(identity.key);
    controllersRef.current.set(targetId, controller);
    publishRecord(targetId, {
      ...identity,
      status: 'loading',
      error: null,
      token,
    });

    try {
      const url = await getSignedUrl(identity.gcsPath, {
        signal: controller.signal,
      });
      const response = await fetch(url, { signal: controller.signal });
      if (!response.ok) {
        throw new Error(`Result download failed with status ${response.status}`);
      }
      const data = await response.json();

      const current = recordsRef.current.get(targetId);
      if (
        controller.signal.aborted
        || scopeKeyRef.current !== identity.scopeKey
        || current?.token !== token
        || current.key !== identity.key
      ) {
        return;
      }

      const accepted = await onHydratedRef.current?.({
        targetId,
        runId: identity.runId,
        generation: identity.generation,
        data,
      });
      const afterApply = recordsRef.current.get(targetId);
      if (
        accepted === false
        || controller.signal.aborted
        || scopeKeyRef.current !== identity.scopeKey
        || afterApply?.token !== token
        || afterApply.key !== identity.key
      ) {
        if (afterApply?.token === token) publishRecord(targetId, null);
        return;
      }

      publishRecord(targetId, {
        ...identity,
        status: 'ready',
        error: null,
        token: null,
      });
    } catch (error) {
      const current = recordsRef.current.get(targetId);
      if (
        controller.signal.aborted
        || error?.name === 'AbortError'
        || scopeKeyRef.current !== identity.scopeKey
        || current?.token !== token
      ) {
        return;
      }
      publishRecord(targetId, {
        ...identity,
        status: 'error',
        error: error instanceof Error ? error.message : 'Complete result download failed',
        token: null,
      });
    } finally {
      if (controllersRef.current.get(targetId) === controller) {
        controllersRef.current.delete(targetId);
      }
    }
  }, [publishRecord]);

  const retry = useCallback((targetId, run) => (
    hydrate(targetId, run, { force: true })
  ), [hydrate]);

  useEffect(() => {
    clear();
  }, [renderedScopeKey, clear]);

  useEffect(() => () => {
    controllersRef.current.forEach(controller => controller.abort());
    controllersRef.current.clear();
    recordsRef.current.clear();
  }, []);

  return {
    states,
    prepare,
    hydrate,
    retry,
    clear,
  };
}
